/**
 * DesignInspector (b4) — right-side panel for a selected design.
 *
 * Whole-design summary metrics from the GeneratedItem snapshot, PLUS a per-ZONE
 * breakdown: each buildable zone's GFZ + GRZ (German planning ratios are
 * computed per zone, not per whole design) and its residential floor count,
 * which is editable. Editing zone floors and regenerating re-rolls the pool with
 * the new per-zone massing — matching how the real app drives the engine.
 *
 * Per-zone GFZ/GRZ aren't in the flat GeneratedItem snapshot, so we load the
 * design into a throwaway World: loadFromDesignGenerator clones the current
 * world and resolves a fresh one, with NO side effects on editor.world or the
 * live board, and we read each zone's ArchitecturalMetrics off it.
 */
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import type { Editor } from "src/types/Editor";
import type { GeneratedItem } from "src/types/GeneratedItem";
import type { RgWorld } from "src/types/RgCorePackage";
import { ZoneType } from "src/types/Zone";
import { ThumbnailRenderer } from "src/types/ThumbnailRenderer";
import { Icon, ACCENT } from "./Icon";

const int = (n: number) => Math.round(n).toLocaleString();
const MAX_FLOORS = 25;

interface ZoneRow {
  id: string;
  label: string;
  buildings: number;
  /** floors that produced THIS design — the baseline for the stepper */
  floors: number;
  gfz: number;
  grz: number;
}

const FRAMES = 24;

const icons = {
  bookmark: <path d="M6 3h12v18l-6-4-6 4z" />,
  lock: (
    <g>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </g>
  ),
  lockOpen: (
    <g>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 0 1 7.5-2" />
    </g>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  map: <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  check: <path d="M5 13l4 4 10-10" />,
  chevL: <path d="M15 6l-6 6 6 6" />,
  chevR: <path d="M9 6l6 6-6 6" />,
  generate: (
    <g>
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
      <path d="M18 14l.7 2.1L21 17l-2.3.9L18 20l-.7-2.1L15 17l2.3-.9z" />
    </g>
  ),
};

export const DesignInspector = observer(function DesignInspector({
  editor,
  item,
  name,
  mode3d,
  inMap = false,
  canFinalize = false,
  bookmarked,
  locked = false,
  onClose,
  onToggleBookmark,
  onToggleLock,
  onRemove,
  onMap,
  onKeep,
  onFinalize,
  onRegenerate,
}: {
  editor: Editor;
  item: GeneratedItem;
  name: string;
  mode3d: boolean;
  inMap?: boolean;
  canFinalize?: boolean;
  bookmarked: boolean;
  locked?: boolean;
  onClose: () => void;
  onToggleBookmark: () => void;
  onToggleLock?: () => void;
  onRemove: () => void;
  onMap: () => void;
  onKeep?: () => void;
  onFinalize?: () => void;
  onRegenerate: (floorOverrides: Record<string, number>) => void;
}) {
  const a = item.architecturalMetrics;
  const cost = item.costMetrics.summary.construction;
  const co2 = item.sustainabilityMetrics.summary.total;
  const mix = a.UnitCategoryMixPerCategory;
  const buildings = a.buildingsCount;

  // Per-zone GFZ/GRZ + floors — loaded from THIS design (see file header).
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [floorEdits, setFloorEdits] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<string | null>(null);
  const [frames, setFrames] = useState<string[]>([]);
  const [frameIdx, setFrameIdx] = useState(0);
  const [rgWorld, setRgWorld] = useState<RgWorld | null>(null);

  // Load the design once: per-zone metrics + the rgWorld used for previews.
  useEffect(() => {
    let cancelled = false;
    setZones([]);
    setFloorEdits({});
    setZonesLoading(true);
    setRgWorld(null);
    const world = editor.world;
    if (!world) {
      setZonesLoading(false);
      return;
    }
    editor.loader
      .loadFromDesignGenerator(item, world)
      .then((dw) => {
        if (cancelled) return;
        const rows: ZoneRow[] = dw.zones
          .filter((z) => z.zoneType === ZoneType.buildable)
          .map((z) => {
            const m = z.metrics;
            return {
              id: z.id,
              label: z.label || "Buildable zone",
              buildings: z.buildings.length,
              floors: z.brief.residentialFloorCount,
              gfz: m.GfzRatio,
              grz: m.LandCoverageTotal,
            };
          });
        setZones(rows);
        setZonesLoading(false);
        setRgWorld(dw.rgWorld);
      })
      .catch(() => {
        if (!cancelled) setZonesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editor, item]);

  // Render the preview for the current mode. 3D = a fast iso frame then the
  // turntable; 2D = the shared top-down thumbnail, no rotation.
  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setFrames([]);
    setFrameIdx(0);
    if (!rgWorld) return;
    if (!mode3d) {
      setPreview(item.thumbnail);
      return;
    }
    ThumbnailRenderer.renderImage(rgWorld, { size: 480 })
      .then((url) => {
        if (!cancelled) setPreview(url);
      })
      .catch(() => {
        if (!cancelled) setPreview(item.thumbnail);
      });
    ThumbnailRenderer.renderFrames(rgWorld, FRAMES, { size: 460 })
      .then((fr) => {
        if (cancelled) return;
        setFrames(fr);
        setFrameIdx(0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [rgWorld, mode3d, item.thumbnail]);

  const spin = (dir: number) => {
    setFrameIdx((i) => (i + dir + frames.length) % frames.length);
  };

  const floorOf = (z: ZoneRow) => floorEdits[z.id] ?? z.floors;
  const dirty = zones.some((z) => floorOf(z) !== z.floors);
  const setFloor = (id: string, v: number) =>
    setFloorEdits((m) => ({ ...m, [id]: Math.max(1, Math.min(MAX_FLOORS, v)) }));

  // Hand the changed per-zone floors back to the board to regenerate this
  // design's revision.
  const applyAndRegenerate = () => {
    const overrides: Record<string, number> = {};
    zones.forEach((z) => {
      const v = floorEdits[z.id];
      if (v != null && v !== z.floors) overrides[z.id] = v;
    });
    onRegenerate(overrides);
  };

  return (
    <div className="h-full w-[360px] shrink-0 border-l border-[#e5e7eb] bg-white flex flex-col">
      {/* header */}
      <div className="flex items-center justify-between px-3 h-12 border-b border-[#e5e7eb] shrink-0">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-[#1f2937] truncate">{name}</div>
          <div className="text-[10px] text-[#9ca3af]">
            {buildings} buildings · {int(a.ApartmentQuantityTotal)} units
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            title={bookmarked ? "Bookmarked" : "Bookmark"}
            onClick={onToggleBookmark}
            className="w-8 h-8 grid place-items-center rounded-sm border"
            style={
              bookmarked
                ? { borderColor: ACCENT, color: ACCENT, background: "rgba(124,92,255,0.1)" }
                : { borderColor: "#e5e7eb", color: "#9ca3af" }
            }
          >
            <Icon d={icons.bookmark} size={15} />
          </button>
          {inMap && onToggleLock && (
            <button
              title={locked ? "Locked — protected from removal" : "Lock"}
              onClick={onToggleLock}
              className="w-8 h-8 grid place-items-center rounded-sm border"
              style={
                locked
                  ? { borderColor: ACCENT, color: ACCENT, background: "rgba(124,92,255,0.1)" }
                  : { borderColor: "#e5e7eb", color: "#9ca3af" }
              }
            >
              <Icon d={locked ? icons.lock : icons.lockOpen} size={15} />
            </button>
          )}
          <button
            title="Close"
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-sm text-[#9ca3af] hover:bg-[#f3f4f6] hover:text-[#4b5563]"
          >
            <Icon d={icons.close} size={16} />
          </button>
        </div>
      </div>

      <div className="grow overflow-y-auto">
        {/* 3D preview — a square block with the design rendered isometric */}
        <div className="p-3">
          <div className="relative w-full aspect-square rounded-md overflow-hidden bg-white border border-[#e5e7eb]">
            <img
              src={frames[frameIdx] ?? preview ?? item.thumbnail}
              alt={name}
              className="w-full h-full object-contain"
            />
            {!preview && (
              <div className="absolute inset-0 grid place-items-center">
                <span className="text-[10px] text-[#9ca3af] bg-white/75 px-2 py-0.5 rounded-full">
                  rendering 3D…
                </span>
              </div>
            )}
            {frames.length > 0 && (
              <>
                <button
                  onClick={() => spin(-1)}
                  title="Rotate left"
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full bg-white/85 border border-[#e5e7eb] text-[#6b7280] shadow-sm hover:bg-white hover:text-[#374151]"
                >
                  <Icon d={icons.chevL} size={15} />
                </button>
                <button
                  onClick={() => spin(1)}
                  title="Rotate right"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full bg-white/85 border border-[#e5e7eb] text-[#6b7280] shadow-sm hover:bg-white hover:text-[#374151]"
                >
                  <Icon d={icons.chevR} size={15} />
                </button>
              </>
            )}
            <span
              className="absolute top-2 left-2 text-[9px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5"
              style={{
                color: "#6b4cf0",
                background: "rgba(255,255,255,0.85)",
                border: "1px solid rgba(124,92,255,0.4)",
              }}
            >
              {mode3d ? "3D" : "2D"}
            </span>
          </div>
        </div>

        {/* whole-design metrics (GFZ/GRZ live per-zone below, not here) */}
        <div className="px-3 pb-3 grid grid-cols-2 gap-x-5 gap-y-0">
          <KV k="Units" v={int(a.ApartmentQuantityTotal)} strong />
          <KV k="Efficiency" v={(a.ResidentialEfficiencyTotal * 100).toFixed(1) + " %"} />
          <KV k="GIA" v={int(a.GIATotal) + " m²"} />
          <KV k="NIA" v={int(a.NIATotal) + " m²"} />
          <KV k="Cost" v={"€" + (cost / 1e6).toFixed(1) + "M"} />
          <KV k="Cost / m²" v={"€" + int(cost / Math.max(1, a.GIATotal))} />
          <KV k="CO₂" v={int(co2) + " t"} />
          <KV k="Max height" v={a.BuildingHeightMax.toFixed(1) + " m"} />
        </div>

        {/* per-zone: floors (editable) + GFZ + GRZ */}
        <div className="px-3 py-3 border-t border-[#e5e7eb]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">
              Zones — floors · GFZ · GRZ
            </span>
            {!zonesLoading && (
              <span className="text-[9px] text-[#9ca3af]">{zones.length} buildable</span>
            )}
          </div>

          {zonesLoading ? (
            <div className="space-y-2">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="h-[92px] rounded-md border border-[#e5e7eb] bg-[#f9fafb] animate-pulse"
                />
              ))}
            </div>
          ) : zones.length === 0 ? (
            <p className="text-[11px] text-[#9ca3af] leading-snug py-2">
              No buildable zones with massing in this design.
            </p>
          ) : (
            <>
              {zones.map((z) => {
                const f = floorOf(z);
                const changed = f !== z.floors;
                return (
                  <div
                    key={z.id}
                    className="rounded-md border border-[#e5e7eb] p-2.5 mb-2 last:mb-0"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] font-medium text-[#374151] truncate">
                        {z.label}
                      </span>
                      <span className="text-[10px] text-[#9ca3af] shrink-0">
                        {z.buildings} building{z.buildings === 1 ? "" : "s"}
                      </span>
                    </div>

                    {/* floors stepper */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] text-[#6b7280]">
                        Floors
                        {changed && (
                          <span className="ml-1.5 text-[9px]" style={{ color: ACCENT }}>
                            was {z.floors}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <StepBtn onClick={() => setFloor(z.id, f - 1)} disabled={f <= 1}>
                          –
                        </StepBtn>
                        <span
                          className="w-8 text-center text-[12px] tabular-nums"
                          style={{ color: changed ? ACCENT : "#1f2937" }}
                        >
                          {f}
                        </span>
                        <StepBtn onClick={() => setFloor(z.id, f + 1)} disabled={f >= MAX_FLOORS}>
                          +
                        </StepBtn>
                      </div>
                    </div>

                    {/* per-zone GFZ / GRZ */}
                    <div className="grid grid-cols-2 gap-2">
                      <ZoneMetric k="GFZ" v={z.gfz.toFixed(2)} hint="floor-area ratio" />
                      <ZoneMetric k="GRZ" v={z.grz.toFixed(2)} hint="site coverage" />
                    </div>
                  </div>
                );
              })}

              {dirty && (
                <button
                  onClick={applyAndRegenerate}
                  className="mt-1 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-white text-[12px] font-semibold"
                  style={{ background: ACCENT }}
                >
                  <Icon d={icons.generate} size={15} /> Regenerate with new floors
                </button>
              )}
              <p className="text-[9px] text-[#9ca3af] leading-snug mt-1.5">
                {dirty
                  ? "Changing floors regenerates the whole board from the updated brief."
                  : "GFZ / GRZ are computed per zone from this design’s massing."}
              </p>
            </>
          )}
        </div>

        {/* unit mix */}
        <div className="px-3 py-3 border-t border-[#e5e7eb]">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af] mb-2">
            Unit mix
          </div>
          {mix.map((m) => (
            <div key={m.name} className="flex items-center gap-2 text-[11px] py-0.5">
              <span className="w-16 text-[#6b7280] truncate">{m.name}</span>
              <span className="grow h-2 rounded-full bg-[#f3f4f6] overflow-hidden">
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${Math.round(m.ratio * 100)}%`, background: ACCENT }}
                />
              </span>
              <span className="text-[#4b5563] tabular-nums w-9 text-right">
                {Math.round(m.ratio * 100)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* footer */}
      <div className="p-2.5 border-t border-[#e5e7eb] bg-[#f9fafb] flex items-center gap-1.5 shrink-0">
        {inMap ? (
          <>
            <button
              onClick={onRemove}
              disabled={locked}
              title={locked ? "Unlock to remove" : "Remove"}
              className={
                "flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-lg border-2 text-[13px] font-semibold " +
                (locked
                  ? "border-[#f3f4f6] text-[#d1d5db] cursor-not-allowed"
                  : "border-[#fca5a5] text-[#dc2626] hover:bg-[#fef2f2]")
              }
            >
              <Icon d={icons.x} size={16} /> Remove
            </button>
            {canFinalize ? (
              <button
                onClick={onFinalize}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-lg text-white text-[13px] font-semibold"
                style={{ background: "#16a34a" }}
              >
                Finalize <Icon d={icons.check} size={16} />
              </button>
            ) : (
              <button
                onClick={onKeep}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-lg border-2 border-[#86efac] text-[#16a34a] hover:bg-[#f0fdf4] text-[13px] font-semibold"
              >
                <Icon d={icons.check} size={16} /> Keep
              </button>
            )}
          </>
        ) : (
          <>
            <button
              onClick={onMap}
              className="grow inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-white text-[12px] font-semibold"
              style={{ background: ACCENT }}
            >
              <Icon d={icons.map} size={15} /> Map view
            </button>
            <button
              onClick={onRemove}
              className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-[#e5e7eb] text-[#6b7280] text-[12px] font-medium hover:text-[#dc2626] hover:border-[#fca5a5]"
            >
              <Icon d={icons.x} size={15} /> Remove
            </button>
          </>
        )}
      </div>
    </div>
  );
});

function KV({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-1 border-b border-dashed border-[#e5e7eb] last:border-0">
      <span className="text-[11px] text-[#9ca3af]">{k}</span>
      <span
        className={
          "tabular-nums " +
          (strong ? "text-[13px] font-semibold text-[#1f2937]" : "text-[12px] text-[#4b5563]")
        }
      >
        {v}
      </span>
    </div>
  );
}

function ZoneMetric({ k, v, hint }: { k: string; v: string; hint: string }) {
  return (
    <div className="rounded-sm bg-[#f9fafb] border border-[#e5e7eb] px-2 py-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[9px] font-semibold uppercase tracking-wide text-[#9ca3af]">{k}</span>
        <span className="text-[13px] tabular-nums text-[#1f2937]">{v}</span>
      </div>
      <div className="text-[9px] text-[#9ca3af] mt-0.5">{hint}</div>
    </div>
  );
}

function StepBtn({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-6 h-6 grid place-items-center rounded-sm border border-[#d1d5db] bg-white text-[#4b5563] text-[13px] leading-none hover:bg-[#f3f4f6] disabled:opacity-40 disabled:hover:bg-white"
    >
      {children}
    </button>
  );
}
